import { FC, useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Card, Dialog, Loading } from '@thirdspacelearning/library';
import { QUERY_STATUS } from '@tsl-frontend/tsl-utilities';

import { SlideControl, SlidesPreview } from './components';
import { localStorage } from '../../utils';

import { getSlides } from '@api';
import { LearningObjective, Slide } from '@types';
import { CurriculumQueryKeys } from '@services';
import { ErrorActionPanel } from '@shared';

const sx = {
    dialog: {
        '& .MuiDialog-container > .MuiPaper-root': {
            backgroundColor: 'transparent',
            boxShadow: 'none',
        },
    },
    previewCard: (fullScreen: boolean) => ({
        display: 'flex',
        borderBottomLeftRadius: 0,
        flexGrow: 1,
        height: fullScreen ? '' : '650px',
    }),
};

export enum ViewType {
    VIEW = 'view',
    EDIT = 'edit',
    ADD = 'add',
}

interface Props {
    onClose: () => void;
    open: boolean;
    startingSlide: number;
    initialViewType: ViewType;
    editable?: boolean;
    learningObjective: LearningObjective;
}

const SlidesPreviewDialog: FC<Props> = ({
    onClose,
    open,
    startingSlide,
    initialViewType,
    editable,
    learningObjective,
}) => {
    const [currentSlideIndex, setCurrentSlideIndex] =
        useState<number>(startingSlide);
    const [fullScreen, setFullScreen] = useState<boolean>(false);
    const [viewType, setViewType] = useState<ViewType>(initialViewType);
    const [slideEdited, setSlideEdited] = useState<boolean>(false);

    const { status, data: slides } = useQuery<Slide[]>({
        queryKey: [`${CurriculumQueryKeys.SLIDES}-${learningObjective.id}`],
        queryFn: () =>
            getSlides(Number(learningObjective.id), { sort: 'position' }),
        enabled: !!learningObjective,
        retry: 1,
        refetchOnWindowFocus: false,
    });

    const checkIfCanExecute = (): boolean => {
        if (slideEdited) {
            const confirmed = window.confirm(
                'You have unsaved changes - are you sure you wish to proceed?',
            );

            return confirmed;
        }

        return true;
    };
    const checkIfCanExecuteCallback = useCallback(checkIfCanExecute, [
        slideEdited,
    ]);

    const changeSlide = (slideIndex: number) => {
        if (checkIfCanExecuteCallback() && slides) {
            setSlideEdited(false);
            setViewType(ViewType.VIEW);
            setCurrentSlideIndex(slideIndex);
            localStorage.remove(`slide-${slides[slideIndex].id}`);
        }
    };

    const closeSlidePreview = () => {
        if (checkIfCanExecuteCallback() && slides) {
            onClose();
            localStorage.remove(`slide-${slides[currentSlideIndex].id}`);
        }
    };

    const handleClose = (
        _e: Event,
        reason: 'backdropClick' | 'escapeKeyDown',
    ) => {
        if (reason === 'backdropClick') {
            return;
        }
        onClose();
    };

    const onEnableEdit = () => {
        setViewType(ViewType.EDIT);
    };

    return (
        <Dialog
            sx={sx.dialog}
            open={open}
            onClose={handleClose}
            aria-labelledby="slidesPreview"
            maxWidth="lg"
            fullScreen={fullScreen}
            fullWidth
        >
            {status === QUERY_STATUS.pending && (
                <Loading loadingMessage="Loading slides..." />
            )}

            {status === QUERY_STATUS.error && (
                <ErrorActionPanel
                    error="There was an error loading the slides"
                    message="Please refresh the page and try again. If the problem persists, please let us know."
                />
            )}

            {status === QUERY_STATUS.success && slides && (
                <>
                    <Card sx={sx.previewCard(fullScreen)}>
                        <SlidesPreview
                            onClose={onClose}
                            fullScreen={fullScreen}
                            slides={slides}
                            learningObjectiveId={learningObjective.id}
                            viewType={viewType}
                            enableEdit={onEnableEdit}
                            editable={editable}
                            learningObjective={learningObjective}
                            setSlideEdited={setSlideEdited}
                            currentSlideIndex={currentSlideIndex}
                            setCurrentSlideIndex={setCurrentSlideIndex}
                        />
                    </Card>

                    {initialViewType !== ViewType.ADD && (
                        <SlideControl
                            currentIndex={currentSlideIndex}
                            onClose={closeSlidePreview}
                            onFullScreen={setFullScreen}
                            onChangeIndex={changeSlide}
                            totalViews={slides ? slides.length : 0}
                            fullScreen={fullScreen}
                        />
                    )}
                </>
            )}
        </Dialog>
    );
};

export default SlidesPreviewDialog;
